/**
 * The game's ONE credits display and top-up control — Home's HUD pill (the owner's own row, 16.9.2026: the gem, the
 * number's plate and the plus baked into the pill's PNG, the live balance placed through the same transform), used as
 * the same element in every view that shows credits (owner 22.9.2026: "Sinne pitää laittaa samaa toi, millä ostetaan
 * noita krediittejä ja mikä on krediittinäyttö, kuin mikä on etusivulla"). One box, one piece, one hit: the plus opens
 * the shop.
 */
import { art } from '../art';
import { Ink } from '../ink';
import { balance, shopPopup } from '../state/store';
import { sfx } from '../audio/sfx';
import CUT from '../layouts/home-cut-2026-09-14/pieces.json';
import LIVE from '../layouts/home-cut-2026-09-14/live.json';

type B = { x: number; y: number; w: number; h: number };
const piece = (id: string): B => { const c = (CUT as (B & { id: string })[]).find((p) => p.id === id); if (!c) throw new Error(`home piece missing: ${id}`); return c; };
/** Home's HUD row: one height for the pill and the bell (72), the pill anchored 14 left of the bell's circle */
export const HUD_H = 72;
export const HUD_BELL: B = { x: 941 - 12 - HUD_H, y: 8, w: HUD_H, h: HUD_H };
export const HUD_PILL: B = (() => { const p = piece('hud.credits'); const w = Math.round(p.w * (HUD_H / p.h)); return { x: HUD_BELL.x - 14 - w, y: 8, w, h: HUD_H }; })();
/** a box that sat on the cut plate, moved and scaled onto the drawn one */
const onPlate = (b: B, from: string, to: B): B => { const p = piece(from); const sx = to.w / p.w, sy = to.h / p.h; return { x: Math.round(to.x + (b.x - p.x) * sx), y: Math.round(to.y + (b.y - p.y) * sy), w: Math.round(b.w * sx), h: Math.round(b.h * sy) }; };
/** the tap area: the whole pill, grown to the touch floor; Home widens it to the bell's edge */
export const HUD_HIT: B = { x: HUD_PILL.x, y: 0, w: HUD_BELL.x - 6 - HUD_PILL.x, h: 116 };

/** the same pill placed by its right margin and vertical centre (a view whose head row has another element to align with —
 *  Match complete: level with MESSAGE CREW, as far from the right edge as that pill is from the left; owner 24.9.2026) */
export function CreditsHud({ hit, at, box, ink }: {
  hit?: B; at?: { right: number; cy: number };
  /** a view's own sizing for the same shared pill (Codex FIX-3 §3): REMATCH draws it at 245 wide, others do not */
  box?: B; ink?: { size: number; pad: number };
}) {
  const pill: B = box ?? (at ? { x: 941 - at.right - HUD_PILL.w, y: at.cy - HUD_H / 2, w: HUD_PILL.w, h: HUD_H } : HUD_PILL);
  const hitBox: B = hit ?? (at ? { x: pill.x - 6, y: Math.max(0, at.cy - 58), w: pill.w + 12, h: 116 } : HUD_HIT);
  const d0 = onPlate((LIVE as { credits: { digits: B; sample: string } }).credits.digits, 'hud.credits', pill);
  /* Codex FIX-3 §3: the balance is Nunito 800 at its own size in this view, not calibrated to the wider lane */
  const d = ink ? { ...d0, cap: ink.size * 0.706 } : d0;
  return (
    <>
      <img class="cut" src={art('/art/home-cut/hud.credits.png')} alt="" draggable={false} style={{ left: pill.x, top: pill.y, width: pill.w, height: pill.h, zIndex: 45 }} data-testid="hud-credits" />
      <Ink box={{ ...d, x: d.x - (ink?.pad ?? 25), w: d.w + (ink?.pad ?? 25) * 2, cap: d.h, text: (LIVE as { credits: { sample: string } }).credits.sample }} text={balance.value.toLocaleString('en-US')} class="cut-credits" align="center" z={46} testid="hud-balance" />
      <button class="cut-hit" type="button" data-testid="hud-plus" aria-label="Add credits" style={{ left: hitBox.x, top: hitBox.y, width: hitBox.w, height: hitBox.h }} onClick={() => { sfx.tap(); shopPopup.value = { onDone: () => { shopPopup.value = null; } }; }} />
    </>
  );
}
